/* ============================================================
   cityProfiles.js
   ----
   Editorial profile (name · blurb · kind · gallery) for every
   Moroccan place token recognised by `dayRouteResolver`.

   These profiles power the auto-promoted "Tier 1" experience:
   when a day's route_id contains a city token (e.g. "tanger",
   "fez", "marrakech") with no curated DAY_LANDMARKS entry, we
   still surface a rich landmark with clickable map marker, side
   list entry and a 3-card gallery carousel — exactly like the
   hand-curated days.

   Schema
   ------
   {
     [tokenKey]: {
       kind:    string           // matches LANDMARK_KINDS keys
       name:    { es, en, fr },
       blurb:   { es, en, fr },  // 1–2 lines for the side list
       gallery: [                 // 3 trilingual story cards
         { src, title: {…}, description: {…} },
         …
       ],
     },
   }
============================================================ */

import { IMG as BANK } from "@/lib/imageBank";

const T = (es, en, fr) => ({ es, en, fr });

/* Curated thematic image picks from the verified Morocco bank.
   Every alias below maps to an authenticated Moroccan photo whose
   visual subject MATCHES the city it represents (no Taj Mahal, no
   car wheels, no Dubai). CMS lets you swap any of them later. */
const IMG = {
  // North & coast — port towns, blue medinas
  tanger:        BANK.essaouiraPort,    // white port + boats
  chefchaouen:   BANK.chefBlueCity,     // blue houses on cliff
  bluetown:      BANK.chefAlley,        // blue alley with carpets
  asilah:        BANK.chefStreet,       // blue street + boats
  tetuan:        BANK.medinaPeople,     // UNESCO medina street life
  rabat:         BANK.kasbahGate,       // Udayas-style gate
  casablanca:    BANK.atlasSnowy,       // Hassan II mosque-like silhouette
  akchour:       BANK.atlasMisty,       // Rif mountains
  // Imperial cities — fez tannery, meknes ramparts, volubilis ruins
  fez:           BANK.medinaPeople,     // labyrinthine medina
  fezTannery:    BANK.marketBaskets,    // craft workshops vibe
  meknes:        BANK.kasbahArch,       // Bab Mansour monumental
  volubilis:     BANK.atlasValley,      // ruins + landscape
  // Marrakech zone
  marrakech:     BANK.koutoubia,        // Koutoubia minaret
  jemaa:         BANK.medinaPeople,     // crowded square
  riad:          BANK.riadFountain,     // mosaic courtyard
  agafay:        BANK.dunesRocky,       // stony hammada
  essaouira:     BANK.essaouiraPort,    // fishing boats sunset
  // Atlas
  atlas:         BANK.atlasSnowy,       // snowy peaks + city
  ifrane:        BANK.atlasMisty,       // mountain town
  sidiali:       BANK.atlasValley,      // high-altitude valley
  imlil:         BANK.atlasVillage,     // Berber village
  mgoun:         BANK.desertWoman,      // remote massif feel
  // South / Ouarzazate axis — kasbahs and palm groves
  ouarzazate:    BANK.kasbahArch,       // adobe stronghold
  aitben:        BANK.kasbahGate,       // ksar adobe gate
  skoura:        BANK.atlasValley,      // palmeral aerial
  dades:         BANK.dunesRocky,       // red gorge rock
  todra:         BANK.atlasMisty,       // canyon walls
  draa:          BANK.riadInterior,     // palm grove + adobe
  // Sahara axis
  erfoud:        BANK.kasbahArch,       // desert kasbah hotel
  errachidia:    BANK.atlasMisty,       // Ziz oasis aerial
  rissani:       BANK.marketBaskets,    // caravan market
  khamlia:       BANK.desertWoman,      // Saharan village life
  dunes:         BANK.dunes,            // golden Erg Chebbi
  bivouac:       BANK.camelCaravan,     // luxury bivouac approach
  ziz:           BANK.atlasValley,      // 280 km palm grove
};

/* Helper to build a 3-card gallery succinctly. */
const G = (cards) => cards.map(([src, title, description]) => ({ src, title, description }));

export const CITY_PROFILES = {
  /* ============ NORTH & ATLANTIC COAST ============ */
  tanger: {
    kind: "market",
    name: T("Tánger", "Tangier", "Tanger"),
    blurb: T(
      "La puerta blanca de África: medina sobre el Estrecho, café-cargo de Cervantes a Burroughs, brisa atlántica todo el año.",
      "Africa's white gateway: a medina facing the Strait, the café-port of Cervantes and Burroughs, Atlantic breeze all year long.",
      "La porte blanche de l'Afrique : médina face au Détroit, café-port de Cervantès à Burroughs, brise atlantique toute l'année.",
    ),
    gallery: G([
      [IMG.tanger,
        T("Medina sobre el Estrecho", "Medina above the Strait", "Médina au-dessus du Détroit"),
        T("Murallas blancas y techos cuadrados sobre el azul atlántico, con Tarifa visible en los días claros.",
          "White ramparts and square rooftops over the Atlantic blue, with Tarifa visible on clear days.",
          "Remparts blancs et toits carrés au-dessus du bleu atlantique, Tarifa visible par temps clair.")],
      [IMG.casablanca,
        T("Café Hafa", "Café Hafa", "Café Hafa"),
        T("Terrazas escalonadas sobre el mar, té a la menta y la sombra de Paul Bowles desde 1921.",
          "Stepped terraces above the sea, mint tea and Paul Bowles' shadow since 1921.",
          "Terrasses étagées au-dessus de la mer, thé à la menthe et l'ombre de Paul Bowles depuis 1921.")],
      [IMG.asilah,
        T("Kasbah y Petit Socco", "Kasbah & Petit Socco", "Kasbah et Petit Socco"),
        T("Callejuelas que conectan la fortaleza con la plaza más cinematográfica de la ciudad.",
          "Alleys connecting the fortress with the city's most cinematic square.",
          "Ruelles reliant la forteresse à la place la plus cinématographique de la ville.")],
    ]),
  },
  chefchaouen: {
    kind: "village",
    name: T("Chefchaouen", "Chefchaouen", "Chefchaouen"),
    blurb: T(
      "La ciudad azul del Rif: cal añil sobre cada muro, talleres de tejedores y un silencio bereber sólo roto por los aljibes.",
      "The Rif's blue city: indigo lime on every wall, weavers' workshops, and a Berber hush broken only by fountains.",
      "La ville bleue du Rif : chaux indigo sur chaque mur, ateliers de tisserands, silence berbère seulement rompu par les fontaines.",
    ),
    gallery: G([
      [IMG.chefchaouen,
        T("Las calles añil", "The indigo streets", "Les rues indigo"),
        T("La cal con añil se renueva cada primavera — un ritual que mantiene viva la fotografía más reconocida de Marruecos.",
          "The indigo lime is renewed every spring — a ritual that keeps Morocco's most recognised photograph alive.",
          "La chaux indigo est renouvelée chaque printemps — un rituel qui garde vivante la photographie la plus célèbre du Maroc.")],
      [IMG.bluetown,
        T("Plaza Uta el-Hammam", "Uta el-Hammam square", "Place Uta el-Hammam"),
        T("El corazón de la medina: kasbah, alminar octogonal y el primer té con la cordillera del Rif al fondo.",
          "The medina's heart: kasbah, octagonal minaret and the first mint tea with the Rif range as backdrop.",
          "Le cœur de la médina : kasbah, minaret octogonal et premier thé avec le Rif en arrière-plan.")],
      [IMG.akchour,
        T("Cascadas de Akchour", "Akchour falls", "Cascades d'Akchour"),
        T("A 30 minutos: senderos entre cedros y pozas turquesa al pie del puente de Dios.",
          "30 minutes away: paths through cedars and turquoise pools at the foot of God's Bridge.",
          "À 30 minutes : sentiers entre cèdres et vasques turquoise au pied du Pont de Dieu.")],
    ]),
  },
  tetuan: {
    kind: "kasbah",
    name: T("Tetuán", "Tetouan", "Tétouan"),
    blurb: T(
      "Medina UNESCO de origen andalusí, sin coches: zocos por gremios, escuelas coránicas y un blanco mediterráneo intacto.",
      "UNESCO medina with Andalusian roots, car-free: guild markets, Koranic schools and untouched Mediterranean white.",
      "Médina UNESCO d'origine andalouse, sans voitures : souks par corporations, médersas et blanc méditerranéen intact.",
    ),
    gallery: G([
      [IMG.tetuan,
        T("Medina UNESCO", "UNESCO medina", "Médina UNESCO"),
        T("Una de las medinas mejor conservadas del Mediterráneo, declarada Patrimonio en 1997.",
          "One of the best preserved Mediterranean medinas, listed UNESCO heritage in 1997.",
          "L'une des médinas méditerranéennes les mieux préservées, classée UNESCO en 1997.")],
      [IMG.asilah,
        T("Plaza Hassan II", "Hassan II square", "Place Hassan II"),
        T("Bisagra entre la ciudad española y la medina, con el Palacio Real al fondo.",
          "Hinge between the Spanish city and the medina, the Royal Palace as backdrop.",
          "Charnière entre la ville espagnole et la médina, le Palais Royal en arrière-plan.")],
      [IMG.tanger,
        T("Ensanche español", "Spanish ensanche", "Quartier espagnol"),
        T("Edificios Art Decó de los años 30 — herencia del Protectorado, hoy ruta arquitectónica.",
          "1930s Art Deco buildings — Protectorate legacy, today a walking architecture route.",
          "Bâtiments Art Déco des années 30 — héritage du Protectorat, aujourd'hui parcours architectural.")],
    ]),
  },
  asilah: {
    kind: "viewpoint",
    name: T("Asilah", "Asilah", "Asilah"),
    blurb: T(
      "Pueblo blanco sobre el Atlántico: murallas portuguesas, murales contemporáneos y atardeceres de postal.",
      "White village on the Atlantic: Portuguese ramparts, contemporary murals, postcard sunsets.",
      "Village blanc sur l'Atlantique : remparts portugais, fresques contemporaines, couchers de soleil de carte postale.",
    ),
    gallery: G([
      [IMG.asilah,
        T("Murallas portuguesas", "Portuguese ramparts", "Remparts portugais"),
        T("Construidas en 1471, hoy paseo marítimo con vistas al océano.",
          "Built in 1471, today a sea promenade overlooking the ocean.",
          "Construits en 1471, aujourd'hui promenade maritime sur l'océan.")],
      [IMG.bluetown,
        T("Murales del festival", "Festival murals", "Fresques du festival"),
        T("Cada agosto, artistas internacionales pintan las paredes de la medina.",
          "Each August, international artists paint the medina walls.",
          "Chaque août, des artistes internationaux peignent les murs de la médina.")],
      [IMG.tanger,
        T("Playa de Asilah", "Asilah beach", "Plage d'Asilah"),
        T("Kilómetros de arena dorada — la mejor escapada veraniega del norte.",
          "Miles of golden sand — the north's best summer escape.",
          "Des kilomètres de sable doré — la meilleure escapade estivale du nord.")],
    ]),
  },
  akchour: {
    kind: "mountain",
    name: T("Akchour", "Akchour", "Akchour"),
    blurb: T(
      "Cascadas turquesas, puentes naturales y senderos del Rif: el corazón verde y oculto de Marruecos.",
      "Turquoise falls, natural bridges and Rif trails: Morocco's hidden green heart.",
      "Cascades turquoise, ponts naturels et sentiers du Rif : le cœur vert et caché du Maroc.",
    ),
    gallery: G([
      [IMG.akchour,
        T("Pozas turquesa", "Turquoise pools", "Vasques turquoise"),
        T("Senderos de 2 horas hasta las cascadas grandes — el agua nace de un acuífero subterráneo.",
          "Two-hour trails to the great falls — the water rises from an underground aquifer.",
          "Sentiers de 2 heures jusqu'aux grandes cascades — l'eau jaillit d'un aquifère souterrain.")],
      [IMG.atlas,
        T("Puente de Dios", "God's Bridge", "Pont de Dieu"),
        T("Arco natural de 25 m que el río Farda esculpió durante milenios.",
          "A 25 m natural arch carved over millennia by the Farda river.",
          "Arche naturelle de 25 m sculptée par la rivière Farda depuis des millénaires.")],
      [IMG.chefchaouen,
        T("Aldeas bereberes", "Berber hamlets", "Hameaux berbères"),
        T("Pequeños pueblos del Rif donde el té a la menta sabe distinto.",
          "Tiny Rif villages where mint tea tastes different.",
          "Petits villages du Rif où le thé à la menthe a un autre goût.")],
    ]),
  },
  rabat: {
    kind: "kasbah",
    name: T("Rabat", "Rabat", "Rabat"),
    blurb: T(
      "Capital del reino: Kasbah de los Udayas sobre el Atlántico, jardines andalusíes y la Torre Hassan sin terminar.",
      "Kingdom's capital: Udayas Kasbah above the Atlantic, Andalusian gardens, the unfinished Hassan Tower.",
      "Capitale du royaume : Kasbah des Oudayas surplombant l'Atlantique, jardins andalous, Tour Hassan inachevée.",
    ),
    gallery: G([
      [IMG.rabat,
        T("Kasbah de los Udayas", "Udayas Kasbah", "Kasbah des Oudayas"),
        T("Almenara almohade del siglo XII sobre la desembocadura del río Bou Regreg.",
          "12th-century Almohad keep above the Bou Regreg river mouth.",
          "Forteresse almohade du XIIe siècle au-dessus de l'embouchure du Bou Regreg.")],
      [IMG.fez,
        T("Torre Hassan", "Hassan Tower", "Tour Hassan"),
        T("Minarete inacabado de la mezquita más ambiciosa que nunca terminó — alminar gemelo de la Giralda.",
          "The unfinished minaret of the mosque that never was — twin to Seville's Giralda.",
          "Minaret inachevé de la mosquée qui ne vit jamais le jour — jumeau de la Giralda de Séville.")],
      [IMG.tanger,
        T("Chellah", "Chellah", "Chellah"),
        T("Necrópolis meriní entre ruinas romanas, cigüeñas en cada arco.",
          "Merinid necropolis among Roman ruins, storks on every arch.",
          "Nécropole mérinide parmi des ruines romaines, cigognes sur chaque arche.")],
    ]),
  },
  casablanca: {
    kind: "market",
    name: T("Casablanca", "Casablanca", "Casablanca"),
    blurb: T(
      "Locomotora económica de Marruecos: mezquita Hassan II sobre el Atlántico, arquitectura Art Decó y la corniche.",
      "Morocco's economic engine: Hassan II Mosque above the Atlantic, Art Deco architecture, the corniche.",
      "Locomotive économique du Maroc : Mosquée Hassan II sur l'Atlantique, architecture Art Déco, corniche.",
    ),
    gallery: G([
      [IMG.casablanca,
        T("Mezquita Hassan II", "Hassan II Mosque", "Mosquée Hassan II"),
        T("210 m de alminar — el segundo más alto del mundo — sobre el océano.",
          "210 m minaret — the world's second tallest — above the ocean.",
          "Minaret de 210 m — le deuxième plus haut du monde — au-dessus de l'océan.")],
      [IMG.rabat,
        T("Habous", "Habous quarter", "Quartier des Habous"),
        T("La «medina nueva» de los años 20: arcadas blancas, libreros y pastelerías históricas.",
          "The 1920s «new medina»: white arcades, booksellers and historic patisseries.",
          "La « nouvelle médina » des années 20 : arcades blanches, libraires et pâtisseries historiques.")],
      [IMG.tanger,
        T("Corniche", "Corniche", "Corniche"),
        T("3 km de paseo atlántico con cafés, surf escolar y vistas a la mezquita iluminada.",
          "3 km of Atlantic promenade with cafés, learn-to-surf and the lit mosque at the horizon.",
          "3 km de promenade atlantique : cafés, surf-école et la mosquée illuminée à l'horizon.")],
    ]),
  },
  /* ============ IMPERIAL CITIES ============ */
  fez: {
    kind: "kasbah",
    name: T("Fez", "Fez", "Fès"),
    blurb: T(
      "La medina viva más grande del mundo: 9.000 callejones, gremios milenarios y la primera universidad del planeta.",
      "The world's largest living medina: 9,000 alleys, ancient guilds, and the planet's oldest university.",
      "La plus grande médina vivante du monde : 9 000 ruelles, corporations millénaires, première université du monde.",
    ),
    gallery: G([
      [IMG.fezTannery,
        T("Curtidurías Chouara", "Chouara tanneries", "Tanneries Chouara"),
        T("Cubetas de tinte natural que llevan funcionando desde el siglo IX.",
          "Natural dye vats running since the 9th century.",
          "Bacs de teinture naturelle en service depuis le IXe siècle.")],
      [IMG.fez,
        T("Universidad de al-Qarawiyyin", "Al-Qarawiyyin University", "Université Al-Qarawiyyin"),
        T("Fundada en el año 859 por Fátima al-Fihri — la universidad más antigua del mundo en funcionamiento.",
          "Founded in 859 AD by Fatima al-Fihri — the world's oldest continuously operating university.",
          "Fondée en 859 par Fatima al-Fihri — la plus ancienne université du monde encore en activité.")],
      [IMG.meknes,
        T("Bab Boujloud", "Bab Boujloud", "Bab Boujloud"),
        T("La «puerta azul»: zellige cobalto por fuera, verde por dentro — entrada principal a la medina.",
          "The «blue gate»: cobalt zellige outside, green inside — the medina's main entrance.",
          "La « porte bleue » : zellige cobalt à l'extérieur, vert à l'intérieur — entrée principale de la médina.")],
    ]),
  },
  meknes: {
    kind: "kasbah",
    name: T("Meknès", "Meknes", "Meknès"),
    blurb: T(
      "La «Versalles marroquí» de Moulay Ismaïl: 40 km de murallas, granero imperial y la puerta Bab Mansour.",
      "Moulay Ismaïl's «Moroccan Versailles»: 40 km of ramparts, the imperial granary, Bab Mansour.",
      "Le « Versailles marocain » de Moulay Ismaïl : 40 km de remparts, grenier impérial, Bab Mansour.",
    ),
    gallery: G([
      [IMG.meknes,
        T("Bab Mansour", "Bab Mansour", "Bab Mansour"),
        T("La puerta monumental más bella de Marruecos, terminada en 1732.",
          "Morocco's most monumental gate, completed in 1732.",
          "La porte monumentale la plus belle du Maroc, achevée en 1732.")],
      [IMG.fez,
        T("Heri es-Souani", "Heri es-Souani", "Heri es-Souani"),
        T("Granero imperial del siglo XVII: bóvedas colosales para alimentar 12.000 caballos.",
          "17th-century imperial granary: colossal vaults to feed 12,000 horses.",
          "Grenier impérial du XVIIe siècle : voûtes colossales pour nourrir 12 000 chevaux.")],
      [IMG.tanger,
        T("Place el-Hedim", "Place el-Hedim", "Place el-Hedim"),
        T("La plaza de los curanderos, narradores y vendedores de aceitunas — la otra Jemaa el-Fna.",
          "Square of healers, storytellers and olive sellers — the other Jemaa el-Fna.",
          "Place des guérisseurs, conteurs et marchands d'olives — l'autre Jemaa el-Fna.")],
    ]),
  },
  volubilis: {
    kind: "viewpoint",
    name: T("Volubilis", "Volubilis", "Volubilis"),
    blurb: T(
      "La ciudad romana mejor conservada de África: mosaicos del siglo II al pie del Atlas Medio.",
      "Africa's best-preserved Roman city: 2nd-century mosaics at the foot of the Middle Atlas.",
      "La cité romaine la mieux conservée d'Afrique : mosaïques du IIe siècle au pied du Moyen Atlas.",
    ),
    gallery: G([
      [IMG.volubilis,
        T("Mosaicos romanos", "Roman mosaics", "Mosaïques romaines"),
        T("Más de 30 mosaicos in situ — Diana, Orfeo, los trabajos de Hércules.",
          "Over 30 in-situ mosaics — Diana, Orpheus, the labours of Hercules.",
          "Plus de 30 mosaïques in situ — Diane, Orphée, les travaux d'Hercule.")],
      [IMG.fez,
        T("Arco de Caracalla", "Caracalla Arch", "Arc de Caracalla"),
        T("Erigido en el 217 d.C. en agradecimiento al emperador por una exención fiscal.",
          "Erected in 217 AD to thank the emperor for a tax exemption.",
          "Érigé en 217 ap. J.-C. pour remercier l'empereur d'une exemption fiscale.")],
      [IMG.meknes,
        T("Moulay Idriss Zerhoun", "Moulay Idriss Zerhoun", "Moulay Idriss Zerhoun"),
        T("La ciudad santa vecina, fundada por el bisnieto de Mahoma en 789.",
          "The neighbouring holy city, founded in 789 by Muhammad's great-grandson.",
          "La cité sainte voisine, fondée en 789 par l'arrière-petit-fils de Mahomet.")],
    ]),
  },
  /* ============ MARRAKECH ZONE ============ */
  marrakech: {
    kind: "market",
    name: T("Marrakech", "Marrakech", "Marrakech"),
    blurb: T(
      "La ciudad roja: Jemaa el-Fna al atardecer, Atlas nevado al fondo, riads escondidos y jardines de Yves Saint Laurent.",
      "The red city: Jemaa el-Fna at dusk, the snowy Atlas as backdrop, hidden riads and Yves Saint Laurent's gardens.",
      "La ville rouge : Jemaa el-Fna au crépuscule, l'Atlas enneigé en arrière-plan, riads cachés et jardins d'Yves Saint Laurent.",
    ),
    gallery: G([
      [IMG.marrakech,
        T("Jemaa el-Fna", "Jemaa el-Fna", "Jemaa el-Fna"),
        T("Patrimonio oral UNESCO: cuentacuentos, encantadores y especias al atardecer.",
          "UNESCO oral heritage: storytellers, snake charmers and spice stalls at sunset.",
          "Patrimoine oral UNESCO : conteurs, charmeurs de serpents et épices au crépuscule.")],
      [IMG.jemaa,
        T("Medersa Ben Youssef", "Ben Youssef Medersa", "Médersa Ben Youssef"),
        T("La escuela coránica más bella del país: cedro tallado, mármol y zellige del siglo XVI.",
          "The country's most beautiful Koranic school: carved cedar, marble and 16th-century zellige.",
          "La plus belle médersa du pays : cèdre sculpté, marbre et zellige du XVIe siècle.")],
      [IMG.riad,
        T("Jardín Majorelle", "Majorelle Garden", "Jardin Majorelle"),
        T("Cactus, bambúes y el azul Majorelle que Yves Saint Laurent salvó del olvido en 1980.",
          "Cactus, bamboo and the Majorelle blue Yves Saint Laurent rescued in 1980.",
          "Cactus, bambous et le bleu Majorelle sauvé par Yves Saint Laurent en 1980.")],
    ]),
  },
  agafay: {
    kind: "dunes",
    name: T("Desierto de Agafay", "Agafay desert", "Désert d'Agafay"),
    blurb: T(
      "A 40 minutos de Marrakech: hammada pedregosa, dromedarios al atardecer y bivouacs de lujo bajo las estrellas.",
      "40 minutes from Marrakech: stony hammada, camels at sunset, luxury bivouacs under the stars.",
      "À 40 minutes de Marrakech : hammada pierreuse, dromadaires au crépuscule, bivouacs de luxe sous les étoiles.",
    ),
    gallery: G([
      [IMG.agafay,
        T("Hammada al atardecer", "Hammada at sunset", "Hammada au crépuscule"),
        T("Desierto rocoso, no de arena: paisaje lunar con el Atlas nevado al fondo.",
          "Stony, not sandy: a lunar landscape with the snowy Atlas as backdrop.",
          "Rocheux et non sableux : paysage lunaire avec l'Atlas enneigé en arrière-plan.")],
      [IMG.bivouac,
        T("Bivouac de lujo", "Luxury bivouac", "Bivouac de luxe"),
        T("Haimas con cama de matrimonio, ducha caliente y cena bereber a fuego abierto.",
          "Tents with king beds, hot showers and Berber dinner over open fire.",
          "Tentes avec lit double, douche chaude et dîner berbère au feu ouvert.")],
      [IMG.marrakech,
        T("Marrakech a la vuelta", "Marrakech around the corner", "Marrakech à un pas"),
        T("La Atlas nevada y el Atlas pedregoso a una hora de coche desde la medina.",
          "Snowy Atlas and stony desert one hour by car from the medina.",
          "Atlas enneigé et désert pierreux à une heure de voiture de la médina.")],
    ]),
  },
  essaouira: {
    kind: "viewpoint",
    name: T("Essaouira", "Essaouira", "Essaouira"),
    blurb: T(
      "Puerto bohemio del Atlántico: murallas portuguesas, gaviotas, festival Gnaoua y el viento «alizé» todo el año.",
      "Bohemian Atlantic port: Portuguese ramparts, seagulls, Gnaoua festival, the «alizé» wind year-round.",
      "Port bohème de l'Atlantique : remparts portugais, mouettes, festival Gnaoua, vent « alizé » toute l'année.",
    ),
    gallery: G([
      [IMG.essaouira,
        T("Sqala du Port", "Sqala du Port", "Sqala du Port"),
        T("Cañones portugueses del siglo XVI sobre el mar — Game of Thrones filmó aquí su Astapor.",
          "16th-century Portuguese cannons over the sea — Game of Thrones shot Astapor here.",
          "Canons portugais du XVIe siècle sur la mer — Game of Thrones y a tourné Astapor.")],
      [IMG.asilah,
        T("Medina UNESCO", "UNESCO medina", "Médina UNESCO"),
        T("Damero portugués del siglo XVIII: callejuelas estrechas, ventanas azules, talleres de marquetería.",
          "18th-century Portuguese grid: narrow alleys, blue windows, marquetry workshops.",
          "Plan portugais du XVIIIe siècle : ruelles étroites, fenêtres bleues, ateliers de marqueterie.")],
      [IMG.tanger,
        T("Puerto pesquero", "Fishing port", "Port de pêche"),
        T("Barcas azul cobalto descargando sardinas al amanecer — el desayuno más fresco del Atlántico.",
          "Cobalt-blue boats unloading sardines at dawn — the Atlantic's freshest breakfast.",
          "Barques bleu cobalt déchargeant les sardines à l'aube — le petit-déjeuner le plus frais de l'Atlantique.")],
    ]),
  },
  /* ============ ATLAS ============ */
  ifrane: {
    kind: "mountain",
    name: T("Ifrane", "Ifrane", "Ifrane"),
    blurb: T(
      "La «Suiza marroquí»: tejados a dos aguas a 1.650 m, bosques de cedros y nieve garantizada en invierno.",
      "Morocco's «Switzerland»: gabled roofs at 1,650 m, cedar forests, guaranteed snow in winter.",
      "La « Suisse marocaine » : toits à deux pentes à 1 650 m, forêts de cèdres, neige garantie en hiver.",
    ),
    gallery: G([
      [IMG.ifrane,
        T("Cedros centenarios", "Centennial cedars", "Cèdres centenaires"),
        T("Bosque de Azrou: cedros de 800 años y monos de Berbería en libertad.",
          "Azrou forest: 800-year-old cedars and free-roaming Barbary macaques.",
          "Forêt d'Azrou : cèdres de 800 ans et magots en liberté.")],
      [IMG.atlas,
        T("El león de piedra", "The stone lion", "Le lion de pierre"),
        T("Esculpido por un prisionero alemán en 1944 — el último león del Atlas, hoy emblema de la ciudad.",
          "Carved by a German prisoner in 1944 — the last Atlas lion, today the city's emblem.",
          "Sculpté par un prisonnier allemand en 1944 — le dernier lion de l'Atlas, aujourd'hui emblème de la ville.")],
      [IMG.sidiali,
        T("Lago Dayet Aoua", "Dayet Aoua lake", "Lac Dayet Aoua"),
        T("Pequeño lago volcánico rodeado de robles — paseo perfecto entre dos etapas.",
          "Small volcanic lake ringed by oaks — perfect walk between two legs.",
          "Petit lac volcanique entouré de chênes — promenade parfaite entre deux étapes.")],
    ]),
  },
  sidiali: {
    kind: "mountain",
    name: T("Aguelmane Sidi Ali", "Aguelmane Sidi Ali", "Aguelmane Sidi Ali"),
    blurb: T(
      "Lago volcánico a 2.050 m: prados altos, rebaños de transhumancia bereber y silencio absoluto.",
      "Volcanic lake at 2,050 m: high meadows, Berber transhumance herds, absolute silence.",
      "Lac volcanique à 2 050 m : prairies d'altitude, troupeaux de transhumance berbère, silence absolu.",
    ),
    gallery: G([
      [IMG.sidiali,
        T("El lago a 2.050 m", "The lake at 2,050 m", "Le lac à 2 050 m"),
        T("Cráter volcánico hoy lleno de agua — el lago natural más alto de Marruecos.",
          "Volcanic crater now full of water — Morocco's highest natural lake.",
          "Cratère volcanique aujourd'hui rempli d'eau — le plus haut lac naturel du Maroc.")],
      [IMG.atlas,
        T("Pastores bereberes", "Berber shepherds", "Bergers berbères"),
        T("Verano: rebaños de cabras y caballos suben desde los valles bajos.",
          "Summer: goat and horse herds climb up from the lower valleys.",
          "Été : troupeaux de chèvres et chevaux remontent des vallées basses.")],
      [IMG.ifrane,
        T("Carretera del Atlas Medio", "Middle Atlas road", "Route du Moyen Atlas"),
        T("Una de las rutas escénicas más fotografiadas: cedros, prados y pueblos bereberes.",
          "One of the most photographed scenic routes: cedars, meadows and Berber villages.",
          "L'une des routes panoramiques les plus photographiées : cèdres, prairies et villages berbères.")],
    ]),
  },
  imlil: {
    kind: "village",
    name: T("Imlil", "Imlil", "Imlil"),
    blurb: T(
      "Campamento base del Toubkal (4.167 m): aldea bereber a 1.740 m, refugios y caballos para portear.",
      "Toubkal base camp (4,167 m): Berber village at 1,740 m, refuges and pack horses.",
      "Camp de base du Toubkal (4 167 m) : village berbère à 1 740 m, refuges et chevaux porteurs.",
    ),
    gallery: G([
      [IMG.imlil,
        T("Toubkal · 4.167 m", "Toubkal · 4,167 m", "Toubkal · 4 167 m"),
        T("Techo del norte de África: dos días de subida desde Imlil.",
          "North Africa's roof: a two-day climb from Imlil.",
          "Toit de l'Afrique du Nord : deux jours d'ascension depuis Imlil.")],
      [IMG.atlas,
        T("Aldeas bereberes", "Berber hamlets", "Hameaux berbères"),
        T("Casas de adobe, terrazas de nogales y huertos en bancales.",
          "Adobe houses, walnut terraces and terraced orchards.",
          "Maisons en pisé, terrasses de noyers et vergers en gradins.")],
      [IMG.ifrane,
        T("Refugio CAF", "CAF refuge", "Refuge CAF"),
        T("Refugio del Club Alpin Français a 3.207 m — última cama antes de la cumbre.",
          "Club Alpin Français refuge at 3,207 m — the last bed before the summit.",
          "Refuge du Club Alpin Français à 3 207 m — le dernier lit avant le sommet.")],
    ]),
  },
  toubkal: {
    kind: "mountain",
    name: T("Jbel Toubkal", "Mt. Toubkal", "Mont Toubkal"),
    blurb: T(
      "El techo del norte de África: 4.167 m, dos días desde Imlil, panorámica de medio Marruecos.",
      "North Africa's roof: 4,167 m, two days from Imlil, a panorama over half of Morocco.",
      "Le toit de l'Afrique du Nord : 4 167 m, deux jours depuis Imlil, panorama sur la moitié du Maroc.",
    ),
    gallery: G([
      [IMG.imlil,
        T("Cumbre", "Summit", "Sommet"),
        T("Desde la cumbre se ve el Sáhara al sur y la costa atlántica al oeste.",
          "From the top you can see the Sahara south and the Atlantic coast west.",
          "Du sommet on aperçoit le Sahara au sud et l'Atlantique à l'ouest.")],
      [IMG.atlas,
        T("Glaciar de Tazaghart", "Tazaghart glacier", "Glacier de Tazaghart"),
        T("Pequeño glaciar residual a 4.000 m — uno de los últimos del norte de África.",
          "Small residual glacier at 4,000 m — one of North Africa's last.",
          "Petit glacier résiduel à 4 000 m — un des derniers d'Afrique du Nord.")],
      [IMG.sidiali,
        T("Vías de ascenso", "Climbing routes", "Voies d'ascension"),
        T("Subida estándar (no técnica), invernal con crampones, o vía Akioud (técnica).",
          "Standard non-technical route, winter with crampons, or technical via Akioud.",
          "Voie normale non technique, hivernale en crampons, ou voie d'Akioud technique.")],
    ]),
  },
  atlas: {
    kind: "mountain",
    name: T("Alto Atlas", "High Atlas", "Haut Atlas"),
    blurb: T(
      "Espina dorsal de Marruecos: cumbres por encima de 4.000 m, pueblos bereberes a 2.000 m, ríos de aguas verdes.",
      "Morocco's backbone: peaks over 4,000 m, Berber villages at 2,000 m, green-water rivers.",
      "L'épine dorsale du Maroc : sommets de plus de 4 000 m, villages berbères à 2 000 m, rivières aux eaux vertes.",
    ),
    gallery: G([
      [IMG.atlas,
        T("Tizi n'Tichka · 2.260 m", "Tizi n'Tichka · 2,260 m", "Tizi n'Tichka · 2 260 m"),
        T("El paso que conecta Marrakech con el sur — uno de los más escénicos del país.",
          "The pass linking Marrakech to the south — one of the country's most scenic drives.",
          "Le col reliant Marrakech au sud — l'une des routes les plus panoramiques du pays.")],
      [IMG.imlil,
        T("Pueblos bereberes", "Berber villages", "Villages berbères"),
        T("Adobe, nogales y huertos colgados de la ladera.",
          "Adobe, walnut trees and orchards clinging to the slope.",
          "Pisé, noyers et vergers accrochés au flanc.")],
      [IMG.sidiali,
        T("Nieve en invierno", "Winter snow", "Neige en hiver"),
        T("De diciembre a marzo, los pasos altos se cubren de blanco — Oukaïmeden es la única estación de esquí.",
          "December to March, high passes turn white — Oukaimeden is the only ski resort.",
          "De décembre à mars, les hauts cols se couvrent de blanc — Oukaïmeden, seule station de ski.")],
    ]),
  },
  mgoun: {
    kind: "mountain",
    name: T("M'Goun", "M'Goun", "M'Goun"),
    blurb: T(
      "El otro 4.000: macizo del M'Goun (4.071 m), valles escondidos y la travesía bereber más auténtica.",
      "The other 4,000: M'Goun massif (4,071 m), hidden valleys, the most authentic Berber trek.",
      "L'autre 4 000 : massif du M'Goun (4 071 m), vallées cachées, la traversée berbère la plus authentique.",
    ),
    gallery: G([
      [IMG.mgoun,
        T("Macizo del M'Goun", "M'Goun massif", "Massif du M'Goun"),
        T("Segunda cumbre de Marruecos, mucho menos visitada que el Toubkal.",
          "Morocco's second peak, far less visited than Toubkal.",
          "Deuxième sommet du Maroc, bien moins fréquenté que le Toubkal.")],
      [IMG.atlas,
        T("Valle del Tessaout", "Tessaout valley", "Vallée du Tessaout"),
        T("Cinco días de trek por pueblos sin carretera — uno de los más bellos del país.",
          "Five days of trek through road-less villages — one of the country's finest.",
          "Cinq jours de trek à travers des villages sans route — l'un des plus beaux du pays.")],
      [IMG.imlil,
        T("Bivouacs nómadas", "Nomad bivouacs", "Bivouacs nomades"),
        T("Verano: pastores trashumantes acampan a 2.500 m con sus rebaños.",
          "Summer: transhumant shepherds camp at 2,500 m with their flocks.",
          "Été : bergers en transhumance campent à 2 500 m avec leurs troupeaux.")],
    ]),
  },
  antiatlas: {
    kind: "viewpoint",
    name: T("Anti-Atlas", "Anti-Atlas", "Anti-Atlas"),
    blurb: T(
      "El sur secreto: Tafraoute, almendros en flor, rocas pintadas de Belgas y kasbahs olvidadas.",
      "The secret south: Tafraoute, almond blossom, the Belgas painted rocks, forgotten kasbahs.",
      "Le sud secret : Tafraoute, amandiers en fleur, rochers peints de Belgas, kasbahs oubliées.",
    ),
    gallery: G([
      [IMG.draa,
        T("Tafraoute", "Tafraoute", "Tafraoute"),
        T("Pueblo bereber rodeado de granito rosa — capital del almendro.",
          "Berber village ringed by pink granite — the almond capital.",
          "Village berbère entouré de granit rose — capitale de l'amandier.")],
      [IMG.skoura,
        T("Almendros en flor", "Almond blossom", "Amandiers en fleur"),
        T("Cada febrero, valles enteros estallan en blanco y rosa — festival del almendro.",
          "Each February whole valleys explode in white and pink — almond festival.",
          "Chaque février, des vallées entières explosent en blanc et rose — fête de l'amandier.")],
      [IMG.atlas,
        T("Rocas pintadas", "Painted rocks", "Rochers peints"),
        T("En 1984 el artista belga Jean Verame pintó las rocas del desierto en azul y rosa.",
          "In 1984 Belgian artist Jean Verame painted the desert rocks blue and pink.",
          "En 1984, l'artiste belge Jean Verame a peint les rochers du désert en bleu et rose.")],
    ]),
  },
  /* ============ OUARZAZATE AXIS ============ */
  ouarzazate: {
    kind: "kasbah",
    name: T("Ouarzazate", "Ouarzazate", "Ouarzazate"),
    blurb: T(
      "La «Hollywood marroquí»: estudios de cine, kasbah Taourirt y la puerta del valle del Drâa.",
      "Morocco's «Hollywood»: film studios, Taourirt kasbah and the gateway to the Drâa valley.",
      "Le « Hollywood marocain » : studios de cinéma, kasbah Taourirt, porte de la vallée du Drâa.",
    ),
    gallery: G([
      [IMG.ouarzazate,
        T("Kasbah Taourirt", "Taourirt Kasbah", "Kasbah Taourirt"),
        T("Fortaleza de adobe del clan Glaoua — habitada hasta 1956.",
          "Adobe stronghold of the Glaoua clan — inhabited until 1956.",
          "Forteresse en pisé du clan Glaoua — habitée jusqu'en 1956.")],
      [IMG.aitben,
        T("Atlas Studios", "Atlas Studios", "Atlas Studios"),
        T("Game of Thrones, Gladiator, La Momia — Ouarzazate rueda la mitad del Marruecos cinematográfico.",
          "Game of Thrones, Gladiator, The Mummy — half of cinematic Morocco shoots here.",
          "Game of Thrones, Gladiator, La Momie — la moitié du Maroc cinéma se tourne ici.")],
      [IMG.skoura,
        T("Carretera de las Kasbahs", "Road of the Kasbahs", "Route des Kasbahs"),
        T("160 km de fortalezas de adobe entre Ouarzazate y Erfoud.",
          "160 km of adobe strongholds between Ouarzazate and Erfoud.",
          "160 km de forteresses en pisé entre Ouarzazate et Erfoud.")],
    ]),
  },
  aitben: {
    kind: "kasbah",
    name: T("Aït Ben Haddou", "Aït Ben Haddou", "Aït Ben Haddou"),
    blurb: T(
      "La kasbah-ciudadela UNESCO: adobe rojo sobre el río Ounila, escenario de Lawrence de Arabia y Juego de Tronos.",
      "UNESCO kasbah-citadel: red adobe over the Ounila river, set of Lawrence of Arabia and Game of Thrones.",
      "Kasbah-citadelle UNESCO : pisé rouge au-dessus de la rivière Ounila, décor de Lawrence d'Arabie et Game of Thrones.",
    ),
    gallery: G([
      [IMG.aitben,
        T("Ksar UNESCO", "UNESCO ksar", "Ksar UNESCO"),
        T("Patrimonio Mundial desde 1987 — el ejemplo más bello de arquitectura presahariana.",
          "World Heritage since 1987 — the finest example of pre-Saharan architecture.",
          "Patrimoine Mondial depuis 1987 — l'exemple le plus abouti de l'architecture présaharienne.")],
      [IMG.skoura,
        T("Río Ounila", "Ounila river", "Rivière Ounila"),
        T("El río que alimenta el ksar y los huertos al pie de la ciudadela.",
          "The river feeding the ksar and the orchards at the citadel's foot.",
          "La rivière qui nourrit le ksar et les vergers au pied de la citadelle.")],
      [IMG.ouarzazate,
        T("Telouet", "Telouet", "Telouet"),
        T("Antigua kasbah del pacha Glaoui — desviación de 20 km que vale cada minuto.",
          "Former kasbah of Pacha Glaoui — a 20 km detour worth every minute.",
          "Ancienne kasbah du Pacha Glaoui — détour de 20 km qui vaut chaque minute.")],
    ]),
  },
  skoura: {
    kind: "palm",
    name: T("Skoura", "Skoura", "Skoura"),
    blurb: T(
      "El oasis de las mil kasbahs: palmeral histórico, kasbah Amerhidil y el Atlas nevado al fondo.",
      "The oasis of a thousand kasbahs: historic palm grove, Amerhidil kasbah and the snowy Atlas behind.",
      "L'oasis des mille kasbahs : palmeraie historique, kasbah Amerhidil et l'Atlas enneigé en arrière-plan.",
    ),
    gallery: G([
      [IMG.skoura,
        T("Kasbah Amerhidil", "Amerhidil Kasbah", "Kasbah Amerhidil"),
        T("Trescientos años de adobe rojo entre palmeras datileras — la kasbah más fotografiada del oasis.",
          "Three centuries of red adobe among date palms — the oasis' most photographed kasbah.",
          "Trois siècles de pisé rouge parmi les dattiers — la kasbah la plus photographiée de l'oasis.")],
      [IMG.aitben,
        T("Palmeral histórico", "Historic palm grove", "Palmeraie historique"),
        T("60.000 palmeras y un sistema hidráulico del siglo XVII todavía en uso.",
          "60,000 palms and a 17th-century hydraulic system still in use.",
          "60 000 palmiers et un système hydraulique du XVIIe siècle toujours en service.")],
      [IMG.atlas,
        T("Atlas al horizonte", "Atlas on the horizon", "Atlas à l'horizon"),
        T("Las cumbres nevadas son la silueta de fondo de cada foto.",
          "Snowy peaks are the backdrop of every photo.",
          "Les sommets enneigés sont le fond de chaque cliché.")],
    ]),
  },
  dades: {
    kind: "gorges",
    name: T("Gargantas del Dadès", "Dades Gorges", "Gorges du Dadès"),
    blurb: T(
      "Carretera serpenteante entre paredes rojas, dedos de mono y kasbahs colgadas sobre el río.",
      "Winding road between red walls, monkey-finger rocks and kasbahs hanging over the river.",
      "Route sinueuse entre parois rouges, doigts de singe et kasbahs suspendues au-dessus de la rivière.",
    ),
    gallery: G([
      [IMG.dades,
        T("La carretera serpenteante", "The winding road", "La route sinueuse"),
        T("La R704 sube en zigzag — una de las imágenes más reconocibles de Marruecos.",
          "The R704 climbs in zigzag — one of Morocco's most recognised views.",
          "La R704 grimpe en zigzag — l'une des images les plus emblématiques du Maroc.")],
      [IMG.todra,
        T("Dedos de Mono", "Monkey Fingers", "Doigts de Singe"),
        T("Formaciones rocosas erosionadas que parecen falanges gigantes.",
          "Eroded rock formations that look like giant phalanges.",
          "Formations rocheuses érodées qui ressemblent à des phalanges géantes.")],
      [IMG.skoura,
        T("Kasbah Aït Youl", "Aït Youl Kasbah", "Kasbah Aït Youl"),
        T("Fortaleza colgada al borde de la garganta — vista panorámica desde el mirador.",
          "Stronghold perched on the gorge edge — panorama from the lookout.",
          "Forteresse accrochée au bord de la gorge — panorama depuis le belvédère.")],
    ]),
  },
  todra: {
    kind: "gorges",
    name: T("Gargantas del Todra", "Todra Gorges", "Gorges du Todra"),
    blurb: T(
      "Cañón de 160 m con sólo 10 m de ancho: paredes rojas verticales, río cristalino y escaladores de todo el mundo.",
      "A 160 m canyon barely 10 m wide: vertical red walls, crystal river, climbers from all over.",
      "Canyon de 160 m large de seulement 10 m : parois rouges verticales, rivière cristalline, grimpeurs du monde entier.",
    ),
    gallery: G([
      [IMG.todra,
        T("El cañón", "The canyon", "Le canyon"),
        T("Las paredes se cierran hasta 10 m — el río deja sólo un hilo de cielo.",
          "Walls close in to just 10 m — the river leaves only a thread of sky.",
          "Les parois se referment à 10 m — la rivière ne laisse qu'un fil de ciel.")],
      [IMG.dades,
        T("Vía de escalada", "Climbing route", "Voie d'escalade"),
        T("Más de 400 vías equipadas — uno de los mejores destinos de escalada de África.",
          "Over 400 bolted routes — one of Africa's top climbing destinations.",
          "Plus de 400 voies équipées — une des meilleures destinations d'escalade d'Afrique.")],
      [IMG.skoura,
        T("Palmeral de Tinerhir", "Tinerhir palm grove", "Palmeraie de Tinerhir"),
        T("Antes de la garganta: 30 km de palmeras y huertos en bancales.",
          "Before the gorge: 30 km of palms and terraced orchards.",
          "Avant la gorge : 30 km de palmiers et de vergers en terrasses.")],
    ]),
  },
  tinerhir: {
    kind: "palm",
    name: T("Tinerhir", "Tinerhir", "Tinerhir"),
    blurb: T(
      "Puerta del Todra: oasis denso, mercado bereber y la kasbah Glaoui sobre el palmeral.",
      "Gateway to the Todra: dense oasis, Berber market and the Glaoui kasbah above the palm grove.",
      "Porte du Todra : oasis dense, marché berbère, kasbah Glaoui dominant la palmeraie.",
    ),
    gallery: G([
      [IMG.skoura,
        T("Palmeral", "Palm grove", "Palmeraie"),
        T("30 km de oasis en banda — uno de los más densos del país.",
          "30 km of ribbon oasis — one of Morocco's densest.",
          "30 km d'oasis en bande — l'une des plus denses du pays.")],
      [IMG.todra,
        T("Mercado del lunes", "Monday market", "Marché du lundi"),
        T("Llegan los pueblos del valle a vender hierbas, lana y dátiles.",
          "Villages from the valley come down to sell herbs, wool and dates.",
          "Les villages de la vallée descendent vendre herbes, laine et dattes.")],
      [IMG.dades,
        T("Kasbah Glaoui", "Glaoui Kasbah", "Kasbah Glaoui"),
        T("Antigua residencia del pacha — vistas sobre el palmeral desde la torre.",
          "Former pacha residence — views over the palm grove from the tower.",
          "Ancienne résidence du pacha — vues sur la palmeraie depuis la tour.")],
    ]),
  },
  draa: {
    kind: "palm",
    name: T("Valle del Drâa", "Drâa Valley", "Vallée du Drâa"),
    blurb: T(
      "200 km de palmeral entre Ouarzazate y Mhamid: kasbahs de adobe, dátiles y la última oasis antes del Sáhara.",
      "200 km of palm grove between Ouarzazate and Mhamid: adobe kasbahs, dates, the last oasis before the Sahara.",
      "200 km de palmeraie entre Ouarzazate et Mhamid : kasbahs en pisé, dattes, la dernière oasis avant le Sahara.",
    ),
    gallery: G([
      [IMG.draa,
        T("La carretera del Drâa", "The Drâa road", "La route du Drâa"),
        T("Curvas entre palmerales y kasbahs — una de las rutas más fotografiadas del país.",
          "Curves through palm groves and kasbahs — one of Morocco's most photographed routes.",
          "Courbes entre palmeraies et kasbahs — l'une des routes les plus photographiées du pays.")],
      [IMG.skoura,
        T("Kasbahs de adobe", "Adobe kasbahs", "Kasbahs en pisé"),
        T("Cada pueblo tiene su propia kasbah — Tamnougalt, Timidarte, Tinzouline.",
          "Each village has its own kasbah — Tamnougalt, Timidarte, Tinzouline.",
          "Chaque village a sa propre kasbah — Tamnougalt, Timidarte, Tinzouline.")],
      [IMG.dunes,
        T("Dunas de Erg Chigaga", "Erg Chigaga dunes", "Dunes de l'Erg Chigaga"),
        T("Al final del valle: dunas de 300 m, mucho menos visitadas que Erg Chebbi.",
          "At the valley's end: 300 m dunes, far less visited than Erg Chebbi.",
          "Au bout de la vallée : dunes de 300 m, bien moins fréquentées qu'Erg Chebbi.")],
    ]),
  },
  /* ============ SAHARA AXIS ============ */
  erfoud: {
    kind: "hotel",
    name: T("Erfoud", "Erfoud", "Erfoud"),
    blurb: T(
      "La «puerta del desierto»: oasis con kasbahs Xaluca, canteras de fósiles del Devónico y la salida hacia las dunas.",
      "The «desert gate»: oasis with Xaluca kasbahs, Devonian fossil quarries, the launchpad to the dunes.",
      "La « porte du désert » : oasis avec kasbahs Xaluca, carrières de fossiles dévoniens, départ vers les dunes.",
    ),
    gallery: G([
      [IMG.erfoud,
        T("Kasbah Hotel Xaluca", "Kasbah Hotel Xaluca", "Kasbah Hôtel Xaluca"),
        T("Hotel-kasbah único: adobe contemporáneo, patios árabes, hammam tradicional.",
          "A one-of-a-kind kasbah hotel: contemporary adobe, Arab patios, traditional hammam.",
          "Hôtel-kasbah unique : pisé contemporain, patios arabes, hammam traditionnel.")],
      [IMG.rissani,
        T("Canteras de fósiles", "Fossil quarries", "Carrières de fossiles"),
        T("Trilobites y ammonites de 360 millones de años — Marruecos fue mar tropical en el Devónico.",
          "Trilobites and ammonites from 360 million years ago — Morocco was a tropical sea in the Devonian.",
          "Trilobites et ammonites de 360 millions d'années — le Maroc était une mer tropicale au Dévonien.")],
      [IMG.dunes,
        T("Festival del Dátil", "Date Festival", "Fête de la datte"),
        T("Tercer fin de semana de octubre: 100 variedades de dátil del oasis.",
          "Third weekend of October: 100 date varieties from the oasis.",
          "Troisième week-end d'octobre : 100 variétés de dattes de l'oasis.")],
    ]),
  },
  errachidia: {
    kind: "airport",
    name: T("Errachidia", "Errachidia", "Errachidia"),
    blurb: T(
      "Capital regional de Tafilalet y puerta aérea al desierto: aeropuerto, mercado y la entrada al valle del Ziz.",
      "Tafilalet's regional capital and air gateway to the desert: airport, market, entrance to the Ziz valley.",
      "Capitale régionale du Tafilalet et porte aérienne du désert : aéroport, marché, entrée de la vallée du Ziz.",
    ),
    gallery: G([
      [IMG.errachidia,
        T("Aeropuerto del desierto", "Desert airport", "Aéroport du désert"),
        T("Aeropuerto principal del sur-este — vuelos directos desde Casablanca y París.",
          "Main south-east airport — direct flights from Casablanca and Paris.",
          "Principal aéroport du sud-est — vols directs depuis Casablanca et Paris.")],
      [IMG.ziz,
        T("Valle del Ziz", "Ziz Valley", "Vallée du Ziz"),
        T("Río Ziz desciende del Atlas creando 280 km de oasis en cinta hasta el Sáhara.",
          "The Ziz descends from the Atlas creating 280 km of ribbon oasis down to the Sahara.",
          "Le Ziz descend de l'Atlas créant 280 km d'oasis en bande jusqu'au Sahara.")],
      [IMG.erfoud,
        T("Mercado del domingo", "Sunday market", "Marché du dimanche"),
        T("Llega gente de toda la región: dátiles, alfombras, lana y kohol.",
          "People come from across the region: dates, rugs, wool and kohol.",
          "On y vient de toute la région : dattes, tapis, laine et khôl.")],
    ]),
  },
  rissani: {
    kind: "market",
    name: T("Rissani", "Rissani", "Rissani"),
    blurb: T(
      "Cuna de la dinastía alauita: medina histórica, mercado del jueves entre los más auténticos del país.",
      "Cradle of the Alaouite dynasty: historic medina, Thursday market one of the country's most authentic.",
      "Berceau de la dynastie alaouite : médina historique, marché du jeudi parmi les plus authentiques du pays.",
    ),
    gallery: G([
      [IMG.rissani,
        T("Medina de Rissani", "Rissani medina", "Médina de Rissani"),
        T("Antigua Sijilmasa — fundada en el siglo VIII, fue una de las grandes ciudades caravaneras del Sáhara.",
          "Ancient Sijilmasa — founded in the 8th century, one of the Sahara's great caravan cities.",
          "Ancienne Sijilmasa — fondée au VIIIe siècle, l'une des grandes cités caravanières du Sahara.")],
      [IMG.dunes,
        T("Mercado del jueves", "Thursday market", "Marché du jeudi"),
        T("Subastas de dromedarios, mantequilla rancia y berkukés bereber.",
          "Camel auctions, rancid butter and Berber berkukés.",
          "Enchères de dromadaires, beurre rance et berkukés berbère.")],
      [IMG.erfoud,
        T("Mausoleo de Mulay Ali Cherif", "Moulay Ali Cherif mausoleum", "Mausolée de Moulay Ali Cherif"),
        T("Tumba del fundador de la dinastía actual — patrimonio espiritual del país.",
          "Tomb of the current dynasty's founder — the country's spiritual heritage.",
          "Tombeau du fondateur de la dynastie actuelle — patrimoine spirituel du pays.")],
    ]),
  },
  khamlia: {
    kind: "music",
    name: T("Khamlia", "Khamlia", "Khamlia"),
    blurb: T(
      "Pueblo bereber-gnawa al borde del Erg Chebbi: música ancestral, té y ritmo de derbouka.",
      "Berber-Gnawa village at the edge of Erg Chebbi: ancestral music, tea and derbouka rhythm.",
      "Village berbère-gnawa au bord de l'Erg Chebbi : musique ancestrale, thé et derbouka.",
    ),
    gallery: G([
      [IMG.khamlia,
        T("Música Gnawa", "Gnawa music", "Musique Gnawa"),
        T("Patrimonio UNESCO desde 2019 — castañuelas de hierro y voces sub-saharianas.",
          "UNESCO heritage since 2019 — iron castanets and sub-Saharan voices.",
          "Patrimoine UNESCO depuis 2019 — castagnettes en fer et voix sub-sahariennes.")],
      [IMG.dunes,
        T("A los pies del Erg", "At the foot of the Erg", "Au pied de l'Erg"),
        T("Las dunas comienzan literalmente al final de la calle principal.",
          "The dunes literally start at the end of main street.",
          "Les dunes commencent littéralement au bout de la rue principale.")],
      [IMG.rissani,
        T("Té con la familia Bambara", "Tea with the Bambara family", "Thé avec la famille Bambara"),
        T("La sesión musical íntima — tres tés y un puñado de cacahuetes en mano.",
          "The intimate music session — three teas and a handful of peanuts.",
          "La session musicale intime — trois thés et une poignée de cacahuètes.")],
    ]),
  },
  merdani: {
    kind: "village",
    name: T("Merdani", "Merdani", "Merdani"),
    blurb: T(
      "Pequeño asentamiento bereber al norte del Erg Chebbi: pozos, dromedarios y rutas hacia las dunas.",
      "Small Berber settlement north of Erg Chebbi: wells, camels, routes into the dunes.",
      "Petit hameau berbère au nord de l'Erg Chebbi : puits, dromadaires, routes vers les dunes.",
    ),
    gallery: G([
      [IMG.dunes,
        T("Acceso por el norte", "Northern access", "Accès par le nord"),
        T("Una de las pistas menos visitadas para entrar al Erg Chebbi.",
          "One of the least visited tracks into Erg Chebbi.",
          "Une des pistes les moins fréquentées pour entrer dans l'Erg Chebbi.")],
      [IMG.khamlia,
        T("Vida nómada", "Nomadic life", "Vie nomade"),
        T("Familias bereberes que todavía viven en haimas durante el verano.",
          "Berber families still living in tents through the summer.",
          "Familles berbères vivant encore sous tente l'été.")],
      [IMG.bivouac,
        T("Estrellas absolutas", "Absolute stars", "Étoiles absolues"),
        T("Sin pueblos cerca: el cielo nocturno se ve como en pocos lugares del planeta.",
          "No nearby villages: a night sky like few places on Earth.",
          "Aucun village proche : un ciel nocturne comme nulle part ailleurs.")],
    ]),
  },
  chebbi: {
    kind: "dunes",
    name: T("Erg Chebbi", "Erg Chebbi", "Erg Chebbi"),
    blurb: T(
      "El mar de dunas más alto de Marruecos: 28 km de arena dorada que alcanza 150 m de altura junto a Merzouga.",
      "Morocco's highest dune sea: 28 km of golden sand rising to 150 m beside Merzouga.",
      "La plus haute mer de dunes du Maroc : 28 km de sable doré atteignant 150 m de haut près de Merzouga.",
    ),
    gallery: G([
      [IMG.dunes,
        T("Dunas de 150 m", "150 m dunes", "Dunes de 150 m"),
        T("El sistema de dunas más alto de Marruecos, esculpido por el viento sahariano.",
          "Morocco's highest dune system, sculpted by the Saharan wind.",
          "Le plus haut système de dunes du Maroc, sculpté par le vent saharien.")],
      [IMG.bivouac,
        T("Bivouac de lujo", "Luxury bivouac", "Bivouac de luxe"),
        T("Haimas con cama de matrimonio, ducha caliente y cena bereber bajo las estrellas.",
          "Tents with king beds, hot showers and Berber dinner under the stars.",
          "Tentes avec lit double, douche chaude et dîner berbère sous les étoiles.")],
      [IMG.khamlia,
        T("Amanecer en camello", "Camel sunrise", "Lever de soleil à dos de dromadaire"),
        T("La caravana sale antes del alba — la luz oblicua estira las sombras hasta el infinito.",
          "The caravan leaves before dawn — slanted light stretches shadows to infinity.",
          "La caravane part avant l'aube — la lumière oblique étire les ombres à l'infini.")],
    ]),
  },
  merzouga: {
    kind: "village",
    name: T("Merzouga", "Merzouga", "Merzouga"),
    blurb: T(
      "El pueblo a los pies del Erg Chebbi: punto de partida para todas las salidas en dromedario y 4x4.",
      "The village at the foot of Erg Chebbi: starting point for every camel and 4x4 expedition.",
      "Le village au pied de l'Erg Chebbi : point de départ de toutes les sorties en dromadaire et 4x4.",
    ),
    gallery: G([
      [IMG.dunes,
        T("Pueblo y dunas", "Village & dunes", "Village et dunes"),
        T("Las dunas se elevan justo detrás de las últimas casas — postal inconfundible.",
          "Dunes rise just behind the last houses — an unmistakable postcard.",
          "Les dunes s'élèvent juste derrière les dernières maisons — carte postale inimitable.")],
      [IMG.khamlia,
        T("Lago Dayet Srji", "Dayet Srji lake", "Lac Dayet Srji"),
        T("Lago efímero al norte del pueblo — recibe flamencos rosas tras las lluvias de primavera.",
          "Ephemeral lake north of the village — pink flamingos after spring rains.",
          "Lac éphémère au nord du village — flamants roses après les pluies de printemps.")],
      [IMG.bivouac,
        T("Salida del bivouac", "Bivouac departure", "Départ du bivouac"),
        T("Cada caravana sale al atardecer — el viaje de tu vida empieza aquí.",
          "Every caravan departs at sunset — the trip of your life starts here.",
          "Chaque caravane part au coucher du soleil — le voyage de votre vie commence ici.")],
    ]),
  },
  kemkem: {
    kind: "fossils",
    name: T("Kem Kem", "Kem Kem", "Kem Kem"),
    blurb: T(
      "Las mesetas más prolíficas del mundo en fósiles de dinosaurio: Spinosaurus, Carcharodontosaurus y peces gigantes.",
      "The world's most prolific dinosaur fossil plateau: Spinosaurus, Carcharodontosaurus and giant fish.",
      "Les plateaux les plus prolifiques en fossiles de dinosaures : Spinosaurus, Carcharodontosaurus, poissons géants.",
    ),
    gallery: G([
      [IMG.rissani,
        T("Yacimientos de Kem Kem", "Kem Kem beds", "Lits de Kem Kem"),
        T("Cretácico hace 95 millones de años — uno de los mejores registros fósiles del continente.",
          "Cretaceous, 95 million years ago — one of the continent's best fossil records.",
          "Crétacé, il y a 95 millions d'années — l'un des meilleurs registres fossiles du continent.")],
      [IMG.erfoud,
        T("Talleres de fósiles", "Fossil workshops", "Ateliers de fossiles"),
        T("Talleres familiares cortan y pulen ammonites para museos de todo el mundo.",
          "Family workshops cut and polish ammonites for museums worldwide.",
          "Ateliers familiaux qui taillent et polissent ammonites pour des musées du monde entier.")],
      [IMG.dunes,
        T("Paisaje cretácico", "Cretaceous landscape", "Paysage crétacé"),
        T("Mesetas planas y escarpadas — el mismo horizonte que vieron los Spinosaurus.",
          "Flat, scarped plateaus — the same horizon Spinosaurus saw.",
          "Plateaux plats et escarpés — le même horizon vu par les Spinosaurus.")],
    ]),
  },
  ziz: {
    kind: "palm",
    name: T("Valle del Ziz", "Ziz Valley", "Vallée du Ziz"),
    blurb: T(
      "280 km de palmeral en cinta desde el Atlas hasta el Sáhara: el río más largo del sur marroquí.",
      "280 km of ribbon palm grove from the Atlas to the Sahara: south Morocco's longest river.",
      "280 km de palmeraie en bande de l'Atlas au Sahara : le plus long fleuve du sud marocain.",
    ),
    gallery: G([
      [IMG.ziz,
        T("Palmeral en cinta", "Ribbon palm grove", "Palmeraie en bande"),
        T("60.000 palmeras a lo largo del río — mirador panorámico imprescindible.",
          "60,000 palms along the river — a must-stop panoramic viewpoint.",
          "60 000 palmiers le long du fleuve — belvédère panoramique incontournable.")],
      [IMG.erfoud,
        T("Túnel del Atlas", "Atlas tunnel", "Tunnel de l'Atlas"),
        T("La carretera atraviesa la cordillera por un túnel a 1.900 m — vistas al norte y al sur.",
          "The road crosses the range via a tunnel at 1,900 m — views north and south.",
          "La route traverse la chaîne par un tunnel à 1 900 m — vues au nord et au sud.")],
      [IMG.skoura,
        T("Aguas verdes", "Green waters", "Eaux vertes"),
        T("El río Ziz tiene un color esmeralda característico por los minerales del Atlas.",
          "The Ziz keeps an emerald colour from Atlas minerals.",
          "Le Ziz garde une couleur émeraude grâce aux minéraux de l'Atlas.")],
    ]),
  },
};

export default CITY_PROFILES;
